import { useId } from 'react';
import type { RegionEstimate } from '../lib/cognitiveEngine';
import { CROWN, TABLE, OUT, css, gemShading, type P } from '../lib/atlasGemModel';

/**
 * The Atlas as a faceted stone, on screen.
 *
 * All geometry and colour live in lib/atlasGemModel, shared with the PNG share
 * card so the two can never drift apart. This file is only the SVG rendering:
 * bloom, grain, volume shading and edges.
 *
 * The stone is an identity object, not the chart. It says "this is yours"; the
 * sorted bars underneath carry the precise reading. It is not labelled and does
 * not pretend to be a lookup table, but every facet's light is real.
 */

export { rampColor } from '../lib/atlasGemModel';

const d = (ps: P[]) => ps.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
const SILHOUETTE = d(OUT);

interface Props {
  regions: RegionEstimate[];
  size?: number;
}

export default function AtlasGem({ regions, size = 200 }: Props) {
  const uid = useId().replace(/:/g, '');
  const { crown, table, anyCharted, lum } = gemShading(regions);

  return (
    <svg
      viewBox="0 0 240 240"
      width={size}
      height={size}
      role="img"
      aria-label="Your cognitive atlas, drawn as a faceted stone. Brighter facets are better charted regions."
      style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}
    >
      <defs>
        {/* Ambient bloom behind the stone. This is what separates it from the
            black surface without drawing a border around it. It takes the
            stone's own colour: an Amber stone must not sit in a violet halo. */}
        <radialGradient id={`${uid}-bloom`}>
          <stop offset="0%" stopColor={css(table)} stopOpacity={anyCharted ? 0.34 : 0.12} />
          <stop offset="58%" stopColor={css(table)} stopOpacity={anyCharted ? 0.1 : 0.04} />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>

        {/* Global volume shading, laid over every facet at once. One gradient
            does the work that per-facet gradients would, and keeps the facet
            fills themselves flat and comparable. */}
        <linearGradient id={`${uid}-vol`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.30" />
          <stop offset="45%" stopColor="#ffffff" stopOpacity="0.02" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.34" />
        </linearGradient>

        {/* Edges of a real stone sit at a grazing angle and go dark. */}
        <radialGradient id={`${uid}-vig`}>
          <stop offset="60%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.32" />
        </radialGradient>

        {/* Nebula grain inside the stone. Screened, so it reads as light caught
            in the material rather than dirt on top of it. Neutral near-white:
            it used to be violet-tinted, which repainted non-violet stones. */}
        <filter id={`${uid}-neb`} x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.019" numOctaves="4" seed="11" result="n" />
          <feColorMatrix
            in="n"
            type="matrix"
            values="0 0 0 0 0.85  0 0 0 0 0.82  0 0 0 0 0.92  0 1.5 0 0 -0.55"
          />
        </filter>

        <clipPath id={`${uid}-clip`}>
          <polygon points={SILHOUETTE} />
        </clipPath>

        <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      <circle cx="120" cy="120" r="119" fill={`url(#${uid}-bloom)`} />

      {/* Soft inner halo, under the facets, so a lit stone glows from within
          rather than looking filled in. */}
      {anyCharted && (
        <polygon points={SILHOUETTE} fill={css(table)} opacity="0.5" filter={`url(#${uid}-glow)`} />
      )}

      <g clipPath={`url(#${uid}-clip)`}>
        {/* Table facet first, so the crown draws over its edges. */}
        <polygon points={d(TABLE)} fill={css(table)} />

        {CROWN.map((facet, i) => (
          <polygon key={i} points={d(facet)} fill={css(crown[i])} />
        ))}

        {/* Interior grain. */}
        <rect
          x="0" y="0" width="240" height="240"
          filter={`url(#${uid}-neb)`}
          opacity={0.1 + 0.55 * lum}
          style={{ mixBlendMode: 'screen' }}
        />

        {/* Volume and edge falloff, over the colour, under the edges. */}
        <rect x="0" y="0" width="240" height="240" fill={`url(#${uid}-vol)`} style={{ mixBlendMode: 'overlay' }} />
        <rect x="0" y="0" width="240" height="240" fill={`url(#${uid}-vig)`} />

        {/* Facet edges. Hairlines, drawn last so they stay crisp over the grain
            and the shading. */}
        <g fill="none" stroke="#ffffff" strokeOpacity="0.14" strokeWidth="0.7" strokeLinejoin="round">
          {CROWN.map((facet, i) => (
            <polygon key={i} points={d(facet)} />
          ))}
          <polygon points={d(TABLE)} strokeOpacity="0.2" />
        </g>
      </g>

      {/* Rim light. Always drawn, at full strength, regardless of the data. This
          is the line that keeps an empty stone looking like a stone. */}
      <polygon
        points={SILHOUETTE}
        fill="none"
        stroke="#d6ccec"
        strokeOpacity="0.36"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      {/* Specular catch on the upper-left edges, the way a cut stone picks up a
          single light source. */}
      <polyline
        points={d([OUT[7], OUT[8], OUT[9], OUT[0]])}
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.55"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
